import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import { leerMensajeGuardado } from "../../utils/volverTrasGuardar";
import BotonVolver from "../../components/BotonVolver";

// Roles con acceso a alguna parte del panel admin.
const TIPOS_POR_ROL = {
  admin_compras:      ["comprador", "repartidor"],
  admin_servicios:    ["supervisora", "asistenta"],
  admin_asistentas:   ["asistenta"],
  admin_atcliente:    ["supervisora"],
  superadmin:         ["superadmin", "admin_compras", "admin_servicios", "admin_asistentas", "admin_atcliente", "supervisora", "comprador", "repartidor", "asistenta", "contabilidad", "admin_llaves"],
};

const TIPO_ICONOS = {
  superadmin: "🛡️", admin_compras: "🛒", admin_servicios: "🏠",
  admin_asistentas: "🧙‍♀️", admin_atcliente: "🤝",
  supervisora: "👩‍💼", comprador: "🛍️", repartidor: "🚚", asistenta: "🧹", contabilidad: "📊", admin_llaves: "🔑",
};
const TIPO_LABELS = {
  superadmin: "Superadmin", admin_compras: "Admin Compras",
  admin_servicios: "Admin Servicios",
  admin_asistentas: "Admin Asistentas", admin_atcliente: "Admin At.Cliente",
  supervisora: "Supervisoras", comprador: "Compradores",
  repartidor: "Repartidores", asistenta: "Asistentas", contabilidad: "Contabilidad", admin_llaves: "Admin Llaves",
};

const TIPO_COLORES = {
  superadmin:        { bg: "linear-gradient(135deg,#9ca3af,#6b7280,#374151)", text: "#fff" },
  admin_compras:     { bg: "linear-gradient(135deg,#38bdf8,#0ea5e9)",  text: "#fff" },
  admin_servicios:   { bg: "linear-gradient(135deg,#34d399,#10b981)",  text: "#fff" },
  admin_asistentas:  { bg: "linear-gradient(135deg,#f97316,#ea580c)",  text: "#fff" },
  admin_atcliente:   { bg: "linear-gradient(135deg,#a78bfa,#7c3aed)",  text: "#fff" },
  supervisora:       { bg: "linear-gradient(135deg,#c084fc,#a855f7)",  text: "#fff" },
  comprador:         { bg: "linear-gradient(135deg,#818cf8,#6366f1)",  text: "#fff" },
  repartidor:        { bg: "linear-gradient(135deg,#4ade80,#22c55e)",  text: "#fff" },
  asistenta:         { bg: "linear-gradient(135deg,#f472b6,#ec4899)",  text: "#fff" },
  contabilidad:      { bg: "linear-gradient(135deg,#fbbf24,#f59e0b)",  text: "#fff" },
  admin_llaves:      { bg: "linear-gradient(135deg,#fde047,#eab308)",  text: "#78350f" },
};

const AdminGestionUsuarios = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser } = useAuth();
  const rolActual = authUser?.tipo || "";
  const rolHeader = rolActual ? { "X-Rol": rolActual } : {};

  // Banner de éxito tras crear/editar un usuario (llega vía navigate state desde FormularioUsuario.js)
  const [successMsg, setSuccessMsg] = useState(() => leerMensajeGuardado("/admin/usuarios") || location.state?.successMsg || null);
  useEffect(() => {
    if (!successMsg) return;
    const t = setTimeout(() => setSuccessMsg(null), 4000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successMsg]);

  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Encargadas (supervisoras asignadas) por admin_asistentas / admin_atcliente,
  // mostradas en la tabla sin necesidad de pulsar "Encargadas".
  const [encargadasMap, setEncargadasMap] = useState({}); // { adminUser: [{supervisora_user, nombre, apellidos}, ...] }
  const [encargadasLoading, setEncargadasLoading] = useState(false);

  // Reset contraseña — modal
  const [resetUser, setResetUser] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [resetPwConfirm, setResetPwConfirm] = useState("");
  const [showResetPw, setShowResetPw] = useState(false);
  const [showResetPwConfirm, setShowResetPwConfirm] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMsg, setResetMsg] = useState(null); // { tipo: 'error'|'ok', texto }

  const abrirReset = (userStr) => {
    setResetUser(userStr);
    setResetPw(""); setResetPwConfirm("");
    setShowResetPw(false); setShowResetPwConfirm(false);
    setResetMsg(null);
  };
  const cerrarReset = () => { setResetUser(null); setResetMsg(null); };

  // Historial de cambios — modal
  const [historialUser, setHistorialUser] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [historialLoading, setHistorialLoading] = useState(false);
  const [historialError, setHistorialError] = useState("");

  const CAMPO_LABEL = {
    user: "Usuario", password: "Contraseña", nombre: "Nombre", apellidos: "Apellidos",
    telefono: "Teléfono", correo: "Correo", tipo: "Tipo", id_empleado: "ID Empleado",
  };

  const abrirHistorial = async (userStr) => {
    setHistorialUser(userStr);
    setHistorial([]);
    setHistorialError("");
    setHistorialLoading(true);
    try {
      const res = await axios.get(`/api/usuarios/${userStr}/historial`, { headers: rolHeader });
      if (!res.data.success) throw new Error(res.data.error || "Error");
      setHistorial(res.data.data || []);
    } catch (e) {
      setHistorialError(e.response?.data?.error || e.message || "No se pudo cargar el historial.");
    } finally {
      setHistorialLoading(false);
    }
  };
  const cerrarHistorial = () => { setHistorialUser(null); setHistorial([]); setHistorialError(""); };

  const handleReset = async (userStr) => {
    setResetMsg(null);
    if (!resetPw) { setResetMsg({ tipo: 'error', texto: 'Introduce la nueva contraseña.' }); return; }
    if (resetPw !== resetPwConfirm) { setResetMsg({ tipo: 'error', texto: 'Las contraseñas no coinciden.' }); return; }
    setResetLoading(true);
    let exito = false;
    try {
      const res = await axios.put(`/api/usuarios/${userStr}`, { password: resetPw });
      if (!res.data.success) throw new Error(res.data.error || "Error");
      exito = true;
      setResetMsg({ tipo: 'ok', texto: 'Contraseña actualizada correctamente.' });
      setResetPw(""); setResetPwConfirm("");
      setTimeout(cerrarReset, 1500);
    } catch (e) {
      setResetMsg({ tipo: 'error', texto: e.response?.data?.error || e.message || 'Error al guardar.' });
    } finally {
      if (!exito) setResetLoading(false);
    }
  };

  // Por defecto solo se listan usuarios activos; este toggle también trae los dados de baja.
  const [mostrarInactivos, setMostrarInactivos] = useState(false);

  // Cargar usuarios al iniciar / al cambiar el toggle de inactivos
  useEffect(() => {
    const fetchUsuarios = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/api/usuarios${mostrarInactivos ? "?incluir_inactivos=1" : ""}`, { headers: rolHeader });
        const payload = res.data;
        // Compatibilidad: antes venía un array directamente; ahora {success, data}
        const lista = Array.isArray(payload) ? payload : payload.data;
        if (!lista) throw new Error("Formato inesperado");
        setUsuarios(lista);
        setError("");
      } catch (err) {
        console.error("Error cargando usuarios", err);
        setError("Error al cargar usuarios.");
      } finally {
        setLoading(false);
      }
    };

    fetchUsuarios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mostrarInactivos]);

  // Cargar encargadas de cada admin_asistentas / admin_atcliente (solo superadmin las ve/gestiona)
  useEffect(() => {
    if (rolActual !== 'superadmin') return;
    const targets = usuarios.filter((u) => u.tipo === 'admin_asistentas' || u.tipo === 'admin_atcliente' || u.tipo === 'admin_servicios');
    if (targets.length === 0) return;
    let cancelado = false;
    setEncargadasLoading(true);
    Promise.all(
      targets.map((u) =>
        axios.get(`/api/usuarios/encargadas/${u.user}`)
          .then((res) => [u.user, res.data?.data || []])
          .catch(() => [u.user, []])
      )
    ).then((entradas) => {
      if (cancelado) return;
      const mapa = {};
      entradas.forEach(([userStr, lista]) => { mapa[userStr] = lista; });
      setEncargadasMap(mapa);
    }).finally(() => { if (!cancelado) setEncargadasLoading(false); });
    return () => { cancelado = true; };
  }, [usuarios, rolActual]);

  const handleEliminar = async (user) => {
    if (!window.confirm(`¿Dar de baja al usuario "${user}"?`)) return;
    try {
      await axios.delete(`/api/usuarios/${user}`, { headers: rolHeader });
      if (mostrarInactivos) {
        setUsuarios((prev) => prev.map((u) => (u.user === user ? { ...u, activo: 0, fecha_baja: new Date().toISOString() } : u)));
      } else {
        setUsuarios((prev) => prev.filter((u) => u.user !== user));
      }
    } catch (e) {
      alert(e.response?.data?.error || "❌ No se pudo dar de baja al usuario.");
    }
  };

  const [busqueda, setBusqueda] = useState("");

  const tiposOrden = TIPOS_POR_ROL[rolActual] || ["supervisora", "comprador", "repartidor", "asistenta", "contabilidad"];

  const ordenarAlfabetico = (lista) => [...lista].sort((a, b) =>
    (a.nombre || a.user || '').localeCompare(b.nombre || b.user || '', 'es', { sensitivity: 'base' })
  );

  // Sin acentos y en minúsculas, para comparar tanto texto libre como iniciales
  const normalizar = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const iniciales = (s) => normalizar(s).trim().split(/\s+/).filter(Boolean).map((w) => w[0]).join('');
  // Coincide solo si ALGUNA palabra empieza por q (no basta con contenerlo, ej. "ma" no debe dar "Xiomara")
  const empiezaAlgunaPalabra = (s) => normalizar(s).trim().split(/\s+/).filter(Boolean).some((w) => w.startsWith(q));

  const q = normalizar(busqueda.trim());
  const usuariosFiltrados = q
    ? usuarios.filter((u) =>
        empiezaAlgunaPalabra(u.user) ||
        empiezaAlgunaPalabra(u.nombre) ||
        empiezaAlgunaPalabra(u.apellidos) ||
        iniciales(`${u.nombre || ''} ${u.apellidos || ''}`).startsWith(q)
      )
    : usuarios;

  const usuariosPorTipo = tiposOrden.map((tipo) => ({
    tipo,
    usuarios: ordenarAlfabetico(usuariosFiltrados.filter((u) => u.tipo === tipo)),
  }));

  // Acordeones: todos cerrados al inicio
  const [abiertos, setAbiertos] = useState({});
  const toggleAcordeon = (tipo) => setAbiertos(prev => ({ ...prev, [tipo]: !prev[tipo] }));
  // Con búsqueda activa (2+ letras) los grupos con resultados se abren solos
  const buscando = q.length >= 2;
  const estaAbierto = (tipo) => buscando || !!abiertos[tipo];

  return (
    <div style={styles.container}>
      <style>{`
        @media (max-width: 640px) { .col-desktop { display: none !important; } }
      `}</style>
      <BotonVolver fallback="/admin" />
      <h2 style={styles.title}>👥 Gestión de Usuarios</h2>
      {successMsg && (
        <div style={styles.successBanner}>{successMsg}</div>
      )}
      {!loading && !error && (
        <>
          <p style={styles.subtitle}>{usuarios.length} usuario{usuarios.length !== 1 ? 's' : ''} en total · haz clic en un grupo para expandirlo</p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap", marginBottom: 12 }}>
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="🔍 Buscar por usuario o nombre…"
              style={{ ...styles.buscador, margin: 0 }}
            />
            <button
              onClick={() => setMostrarInactivos((v) => !v)}
              style={mostrarInactivos ? styles.toggleInactivosActivo : styles.toggleInactivos}
            >
              {mostrarInactivos ? "✓ Mostrando también no activos" : "También no activos"}
            </button>
            <button
              style={styles.historialGeneral}
              onClick={() => navigate("/admin/usuarios/historial")}
              onMouseOver={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 16px rgba(94, 53, 177, 0.45)"; }}
              onMouseOut={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 10px rgba(94, 53, 177, 0.35)"; }}
            >
              📜 Historial
            </button>
          </div>
        </>
      )}

      {/* Modal restablecer contraseña */}
      {resetUser && ReactDOM.createPortal(
        <div style={styles.overlay} onClick={resetLoading ? undefined : cerrarReset}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <span style={styles.modalIcon}>🔑</span>
              <div>
                <div style={styles.modalTitle}>Restablecer contraseña</div>
                <div style={styles.modalSub}>Usuario: <strong>{resetUser}</strong></div>
              </div>
              <button style={{ ...styles.closeBtn, visibility: resetLoading ? 'hidden' : 'visible' }} onClick={cerrarReset} disabled={resetLoading} title="Cerrar">✕</button>
            </div>

            {resetMsg && (
              <div style={{ ...styles.msgBox, ...(resetMsg.tipo === 'ok' ? styles.msgOk : styles.msgErr) }}>
                {resetMsg.tipo === 'ok' ? '✅ ' : '⚠️ '}{resetMsg.texto}
              </div>
            )}

            <div style={{ padding: "0 20px" }}>
            <label style={styles.modalLabel}>Nueva contraseña *</label>
            <div style={styles.pwRow}>
              <input
                type={showResetPw ? "text" : "password"}
                value={resetPw}
                onChange={e => { setResetPw(e.target.value); setResetMsg(null); }}
                style={styles.pwInput}
                autoComplete="new-password"
                autoFocus
                placeholder="Mínimo 3 caracteres"
                disabled={resetLoading}
              />
              <button type="button" style={styles.eyeBtn} onClick={() => setShowResetPw(v => !v)} title={showResetPw ? 'Ocultar' : 'Mostrar'}>
                {showResetPw ? "🙈" : "👁"}
              </button>
            </div>

            <label style={styles.modalLabel}>Confirmar contraseña *</label>
            <div style={styles.pwRow}>
              <input
                type={showResetPwConfirm ? "text" : "password"}
                value={resetPwConfirm}
                onChange={e => { setResetPwConfirm(e.target.value); setResetMsg(null); }}
                style={{
                  ...styles.pwInput,
                  borderColor: resetPwConfirm && resetPw !== resetPwConfirm ? '#dc3545' : resetPwConfirm && resetPw === resetPwConfirm ? '#28a745' : undefined,
                }}
                autoComplete="new-password"
                placeholder="Repite la contraseña"
                disabled={resetLoading}
              />
              <button type="button" style={styles.eyeBtn} onClick={() => setShowResetPwConfirm(v => !v)} title={showResetPwConfirm ? 'Ocultar' : 'Mostrar'}>
                {showResetPwConfirm ? "🙈" : "👁"}
              </button>
            </div>
            {resetPwConfirm && resetPw !== resetPwConfirm && (
              <div style={styles.fieldErr}>Las contraseñas no coinciden</div>
            )}
            {resetPwConfirm && resetPw === resetPwConfirm && (
              <div style={styles.fieldOk}>✔ Las contraseñas coinciden</div>
            )}
            </div>

            <div style={styles.modalFooter}>
              <button style={{ ...styles.btnCancelar, opacity: resetLoading ? 0.5 : 1 }} onClick={cerrarReset} disabled={resetLoading}>Cancelar</button>
              <button
                style={{ ...styles.btnGuardar, opacity: resetLoading ? 0.7 : 1 }}
                disabled={resetLoading}
                onClick={() => handleReset(resetUser)}
              >
                {resetLoading ? 'Guardando…' : '💾 Guardar contraseña'}
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Modal historial de cambios */}
      {historialUser && ReactDOM.createPortal(
        <div style={styles.overlay} onClick={cerrarHistorial}>
          <div style={{ ...styles.modal, maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <span style={styles.modalIcon}>🕘</span>
              <div>
                <div style={styles.modalTitle}>Historial de cambios</div>
                <div style={styles.modalSub}>Usuario: <strong>{historialUser}</strong></div>
              </div>
              <button style={styles.closeBtn} onClick={cerrarHistorial} title="Cerrar">✕</button>
            </div>

            <div style={{ padding: "0 20px 16px", maxHeight: "60vh", overflowY: "auto" }}>
              {historialLoading ? (
                <p style={{ textAlign: "center", color: "#666" }}>Cargando…</p>
              ) : historialError ? (
                <div style={{ ...styles.msgBox, ...styles.msgErr }}>⚠️ {historialError}</div>
              ) : historial.length === 0 ? (
                <p style={{ textAlign: "center", color: "#94a3b8", fontSize: 13 }}>Sin cambios registrados todavía.</p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {historial.map((h) => (
                    <div key={h.id} style={styles.historialItem}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                        <strong style={{ fontSize: 13, color: h.tipo_evento === "BAJA" ? "#b91c1c" : h.tipo_evento === "ALTA" ? "#15803d" : "#1d4ed8" }}>
                          {h.tipo_evento === "ALTA" ? "✅ Usuario creado" : h.tipo_evento === "BAJA" ? "🚫 Dado de baja" : `✏️ ${CAMPO_LABEL[h.campo] || h.campo || "Cambio"}`}
                        </strong>
                        <span style={{ fontSize: 11, color: "#94a3b8" }}>{new Date(h.fecha_hora).toLocaleString("es-ES")}</span>
                      </div>
                      {h.tipo_evento === "MODIFICACION" && (
                        h.campo === "password" ? (
                          <div style={{ fontSize: 12.5, color: "#475569" }}>🔒 Contraseña cambiada (hash guardado, nunca en texto plano)</div>
                        ) : (
                          <div style={{ fontSize: 12.5, color: "#475569" }}>
                            {h.valor_anterior || "—"} → <strong>{h.valor_nuevo || "—"}</strong>
                          </div>
                        )
                      )}
                      {h.editor_email && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>por {h.editor_email}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      , document.body)}

      {loading ? (
        <p style={{ textAlign: 'center', color: '#666', marginTop: 40 }}>Cargando usuarios...</p>
      ) : error ? (
        <p style={{ color: "red", textAlign: 'center' }}>{error}</p>
      ) : (
        <div style={styles.acordeonesWrap}>
          {usuariosPorTipo.map((grupo) =>
            grupo.usuarios.length > 0 ? (
              <div key={grupo.tipo} style={styles.acordeon}>
                {(() => {
                  const c = TIPO_COLORES[grupo.tipo] || { bg: "#475569", text: "#fff" };
                  return (
                    <button
                      style={{ ...styles.acordeonHeader, background: c.bg }}
                      onClick={() => toggleAcordeon(grupo.tipo)}
                    >
                      <span style={styles.acordeonIcono}>{TIPO_ICONOS[grupo.tipo] || '👤'}</span>
                      <span style={{ ...styles.acordeonTipo, color: c.text }}>
                        {TIPO_LABELS[grupo.tipo] || grupo.tipo}
                      </span>
                      <span style={{ ...styles.acordeonBadge, background: "rgba(255,255,255,0.25)", color: c.text }}>{grupo.usuarios.length}</span>
                      <span style={{ ...styles.acordeonChevron, color: c.text, transform: estaAbierto(grupo.tipo) ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                    </button>
                  );
                })()}

                {estaAbierto(grupo.tipo) && (() => {
                  const mostrarColEncargadas = rolActual === 'superadmin' && (grupo.tipo === 'admin_asistentas' || grupo.tipo === 'admin_atcliente' || grupo.tipo === 'admin_servicios');
                  return (
                  <div style={styles.acordeonBody}>
                    <table style={styles.table}>
                      <thead>
                        <tr style={styles.theadRow}>
                          <th style={styles.th}>Usuario</th>
                          <th style={styles.th}>Contraseña</th>
                          <th className="col-desktop" style={styles.th}>Nombre</th>
                          <th className="col-desktop" style={styles.th}>Apellidos</th>
                          <th className="col-desktop" style={styles.th}>Teléfono</th>
                          <th className="col-desktop" style={styles.th}>Correo</th>
                          <th className="col-desktop" style={styles.th}>ID Empleado</th>
                          {mostrarColEncargadas && <th style={styles.th}>Encargadas</th>}
                          <th style={styles.th}>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {grupo.usuarios.map((u) => (
                          <tr key={u.user} style={u.activo === 0 ? styles.userRowBaja : styles.userRow}>
                            <td style={styles.td}>
                              <strong>{u.user}</strong>
                              {u.activo === 0 && <span style={styles.badgeBaja}>BAJA</span>}
                            </td>
                            <td style={styles.td}>
                              <button
                                title="Restablecer contraseña"
                                onClick={() => abrirReset(u.user)}
                                style={styles.keyBtn}
                              >
                                <span style={{ fontSize: 15 }}>🔑</span>
                                <span style={styles.keyBtnLabel}>Contraseña</span>
                              </button>
                            </td>
                            <td className="col-desktop" style={styles.td}>{u.nombre}</td>
                            <td className="col-desktop" style={styles.td}>{u.apellidos}</td>
                            <td className="col-desktop" style={styles.td}>{u.telefono}</td>
                            <td className="col-desktop" style={{ ...styles.td, wordBreak: "break-all" }}>{u.correo ? <a href={`mailto:${u.correo}`} style={{ color: "#2563eb" }}>{u.correo}</a> : <span style={{ color: "#cbd5e1" }}>—</span>}</td>
                            <td className="col-desktop" style={styles.td}>{u.id_empleado ?? "—"}</td>
                            {mostrarColEncargadas && (
                              <td style={styles.td}>
                                {encargadasLoading && !encargadasMap[u.user] ? (
                                  <span style={{ color: '#94a3b8', fontSize: 12 }}>Cargando…</span>
                                ) : (encargadasMap[u.user] || []).length === 0 ? (
                                  <span style={{ color: '#94a3b8', fontSize: 12, fontStyle: 'italic' }}>Sin asignar</span>
                                ) : (
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 220 }}>
                                    {(encargadasMap[u.user] || []).map((e) => (
                                      <span key={e.supervisora_user} style={styles.pillEncargada}>{e.supervisora_user}</span>
                                    ))}
                                  </div>
                                )}
                              </td>
                            )}
                            <td style={styles.td}>
                              {rolActual === 'superadmin' && (u.tipo === 'admin_asistentas' || u.tipo === 'admin_atcliente' || u.tipo === 'admin_servicios') && (
                                <button style={styles.encargadas} onClick={() => navigate(`/admin/encargadas/${u.user}`)}>👥 Encargadas</button>
                              )}
                              <button style={styles.edit} onClick={() => navigate(`/usuarios/editar/${u.user}`)}>✏️ Editar</button>
                              <button style={styles.historial} onClick={() => abrirHistorial(u.user)}>🕘 Historial</button>
                              {u.activo !== 0 && (
                                <button style={styles.delete} onClick={() => handleEliminar(u.user)}>🗑 Dar de baja</button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  );
                })()}
              </div>
            ) : null
          )}
        </div>
      )}

      <button style={styles.add} onClick={() => navigate("/usuarios/nuevo")}>➕ Añadir Usuario</button>
    </div>
  );
};

const styles = {
  container: { padding: "24px 20px 80px", fontFamily: "Segoe UI, sans-serif", maxWidth: 960, margin: "0 auto" },
  title: { marginBottom: 8, color: "#1a1a2e", fontSize: 26, fontWeight: 700, textAlign: "center" },
  subtitle: { textAlign: "center", color: "#888", fontSize: 13, marginBottom: 12 },
  successBanner: { maxWidth: 420, margin: "0 auto 16px", padding: "12px 16px", borderRadius: 10, background: "#d1e7dd", color: "#0a3622", border: "1px solid #a3cfbb", fontSize: 13.5, fontWeight: 700, textAlign: "center" },
  buscador: { display: "block", width: "100%", maxWidth: 420, margin: "0 auto 24px", padding: "10px 14px", fontSize: 14, border: "1px solid #ccc", borderRadius: 8, outline: "none", boxSizing: "border-box" },

  // Acordeones
  acordeonesWrap: { display: "flex", flexDirection: "column", gap: 12 },
  acordeon: { borderRadius: 14, overflow: "hidden", boxShadow: "0 2px 10px rgba(0,0,0,0.10)" },
  acordeonHeader: { width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "18px 22px", border: "none", cursor: "pointer", textAlign: "left" },
  acordeonIcono: { fontSize: 26 },
  acordeonTipo: { flex: 1, fontWeight: 800, fontSize: 17, letterSpacing: "0.01em" },
  acordeonBadge: { borderRadius: 20, padding: "3px 13px", fontSize: 13, fontWeight: 700 },
  acordeonChevron: { fontSize: 13, transition: "transform 0.25s" },
  acordeonBody: { overflowX: "auto", background: "#fff" },

  // Tabla
  table: { width: "100%", borderCollapse: "collapse" },
  theadRow: { background: "#f1f5f9" },
  th: { padding: "10px 14px", fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em", textAlign: "left", borderBottom: "1px solid #e2e8f0" },
  td: { padding: "10px 14px", fontSize: 14, color: "#334155", borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" },
  userRow: { background: "#fff", transition: "background 0.15s" },
  userRowBaja: { background: "#f2f2f2", opacity: 0.65 },
  badgeBaja: { display: "inline-block", marginLeft: 8, padding: "2px 8px", fontSize: 11, fontWeight: 700, color: "#fff", backgroundColor: "#e74c3c", borderRadius: 10, verticalAlign: "middle" },
  toggleInactivos: { padding: "10px 16px", borderRadius: 10, border: "1.5px solid #e2e8f0", background: "#fff", color: "#64748b", fontWeight: 700, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" },
  toggleInactivosActivo: { padding: "10px 16px", borderRadius: 10, border: "1.5px solid #94a3b8", background: "#475569", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" },
  historialGeneral: {
    padding: "10px 20px",
    background: "linear-gradient(135deg, #7e57c2, #5e35b1)",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 13,
    whiteSpace: "nowrap",
    boxShadow: "0 4px 10px rgba(94, 53, 177, 0.35)",
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
  },

  // Acciones
  edit: { padding: "5px 12px", backgroundColor: "#fef3c7", border: "1px solid #fcd34d", color: "#92400e", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600, marginRight: 6 },
  delete:     { padding: "5px 12px", backgroundColor: "#fee2e2", border: "1px solid #fca5a5", color: "#991b1b", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 },
  encargadas: { padding: "5px 12px", backgroundColor: "#ede9fe", border: "1px solid #c4b5fd", color: "#5b21b6", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600, marginRight: 4 },
  historial: { padding: "5px 12px", backgroundColor: "#e0f2fe", border: "1px solid #7dd3fc", color: "#075985", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600, marginRight: 6 },
  historialItem: { padding: "10px 12px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8 },
  pillEncargada: { padding: "3px 10px", backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", color: "#15803d", borderRadius: 20, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" },
  add: { position: "fixed", bottom: 28, right: 28, padding: "14px 22px", fontSize: 15, fontWeight: 700, backgroundColor: "#16a34a", color: "#fff", border: "none", borderRadius: 50, cursor: "pointer", boxShadow: "0 4px 14px rgba(22,163,74,0.4)" },

  // Botón contraseña
  keyBtn: { display: "inline-flex", alignItems: "center", gap: 5, background: "#fff8e1", border: "1px solid #ffc107", borderRadius: 6, cursor: "pointer", padding: "4px 10px", fontSize: 13, color: "#856404", fontWeight: 600 },
  keyBtnLabel: { fontSize: 12 },

  // Modal
  pwRow: { display: "flex", alignItems: "center", gap: 6, margin: "6px 0" },
  pwInput: { flex: 1, padding: "8px 10px", fontSize: 14, border: "1px solid #ccc", borderRadius: 6, outline: "none" },
  eyeBtn: { background: "none", border: "1px solid #ccc", borderRadius: 6, cursor: "pointer", padding: "4px 8px", fontSize: 15 },
  modalLabel: { display: "block", margin: "14px 0 4px", fontWeight: 600, fontSize: 13, color: "#444" },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 32 },
  modal: { background: "#fff", borderRadius: 12, padding: "0", minWidth: 340, maxWidth: 440, width: "92%", boxShadow: "0 12px 40px rgba(0,0,0,0.25)", overflow: "hidden" },
  modalHeader: { display: "flex", alignItems: "center", gap: 12, background: "#f8f9fa", borderBottom: "1px solid #e0e0e0", padding: "16px 20px" },
  modalIcon: { fontSize: 28 },
  modalTitle: { fontWeight: 700, fontSize: 16, color: "#222" },
  modalSub: { fontSize: 13, color: "#666", marginTop: 2 },
  closeBtn: { marginLeft: "auto", background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#888", lineHeight: 1, padding: "2px 6px" },
  modalFooter: { display: "flex", gap: 10, justifyContent: "flex-end", padding: "16px 20px", borderTop: "1px solid #e0e0e0", background: "#f8f9fa", marginTop: 16 },
  btnCancelar: { padding: "8px 18px", border: "1px solid #ccc", borderRadius: 6, background: "#fff", cursor: "pointer", fontSize: 14 },
  btnGuardar: { padding: "8px 20px", border: "none", borderRadius: 6, background: "#0d6efd", color: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 600 },
  msgBox: { borderRadius: 6, padding: "9px 14px", fontSize: 13, margin: "12px 20px 0" },
  msgOk: { background: "#d1e7dd", color: "#0a3622", border: "1px solid #a3cfbb" },
  msgErr: { background: "#f8d7da", color: "#58151c", border: "1px solid #f1aeb5" },
  fieldErr: { fontSize: 12, color: "#dc3545", marginTop: 2, marginBottom: 4 },
  fieldOk: { fontSize: 12, color: "#28a745", marginTop: 2, marginBottom: 4 },
};

export default AdminGestionUsuarios;
